import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { Task, UpdateStatus } from '@css-xp/apd-common';
import { Icon } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { cancelRequest as CANCEL_REQUEST } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const CancelRequestModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [cancelRequest] = useMutation<{ cancelRequest: { status: UpdateStatus } }>(CANCEL_REQUEST);
  const userProfile = useApplicationContext();
  const userID = userProfile?.userId?.split('@')?.[0];

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'cancel-request-modal-notification'
  );

  const handleFormSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      setLoading(true);
      try {
        const result = await cancelRequest({
          variables: {
            processInstanceId: task.processInstanceId,
            id: userID,
            comment,
          },
        });
        const status = result?.data?.cancelRequest?.status ?? '';
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        if (status === 'OK') {
          onComplete?.();
          renderSuccessNotification(
            'Task complete - request cancelled',
            <>
              <p className="action-modal-notification">
                Your request {task.taskNumber} has been cancelled and moved to{' '}
                <span className="bold-text">approval requests history</span>.
              </p>
              <Link to={{ hash: '#history' }} className="inner-text">
                View request
              </Link>
            </>
          );
        }
      } catch (e: any) {
        renderErrorNotification(
          'We’re unable to cancel your request',
          <ErrorNotificationMessage errorMessage={e.message} />
        );
      } finally {
        setLoading(false);
      }
    },
    [task.taskId]
  );

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="cancel-request-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="icon">
                    <Icon type="warning" size="medium" />
                  </span>
                  <span className="modal-title">{ModalTitle.CANCEL_REQUEST}</span>
                </div>
              }
            />
            <p>
              Your cancellation and comments will be added to the request in the Slack channel{' '}
              <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
            </p>
            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="comment"
                label="Internal comment"
                isRequired={true}
                validate={(value: any) => (value && value.trim().length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <TextArea {...fieldProps} placeholder="Add comment" testId="comment-input" isInvalid={!!error} />
                    {error && <ErrorMessage testId="comment-input-error">This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Close
                </Button>
                <LoadingButton appearance="warning" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Continue
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default CancelRequestModal;
